import { useEffect, useState, useCallback } from "react";
import { adminApi } from "@/lib/adminApi";
import { fmtDate, fmtMoney } from "@/pages/admin/util";
import { Loader2, RefreshCw, Plus, Pencil, Trash2, BadgePoundSterling } from "lucide-react";
import { toast } from "sonner";

const EMPTY = {
  title: "",
  slug: "",
  category: "Roof",
  low_gbp: 0,
  typical_gbp: 0,
  high_gbp: 0,
  description: "",
  factors_affecting_price: "",
  red_flags: "",
  status: "PUBLISHED",
};

const CATEGORIES = ["Roof", "Damp", "Underfloor", "Structural", "Plumbing", "Electrical", "Decorative", "Insurance-related"];

function toForm(r) {
  if (!r) return { ...EMPTY };
  return {
    title: r.title || "",
    slug: r.slug || "",
    category: r.category || "Roof",
    low_gbp: r.low_gbp ?? 0,
    typical_gbp: r.typical_gbp ?? 0,
    high_gbp: r.high_gbp ?? 0,
    description: r.description || "",
    factors_affecting_price: (r.factors_affecting_price || []).join("\n"),
    red_flags: (r.red_flags || []).join("\n"),
    status: r.status || "PUBLISHED",
  };
}

function toPayload(form) {
  return {
    title: form.title.trim(),
    slug: form.slug.trim(),
    category: form.category.trim(),
    low_gbp: Number(form.low_gbp) || 0,
    typical_gbp: Number(form.typical_gbp) || 0,
    high_gbp: Number(form.high_gbp) || 0,
    description: form.description.trim(),
    factors_affecting_price: form.factors_affecting_price.split("\n").map((s) => s.trim()).filter(Boolean),
    red_flags: form.red_flags.split("\n").map((s) => s.trim()).filter(Boolean),
    status: form.status,
  };
}

export default function AdminIntelligenceRepairCosts() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await adminApi.get("/repair-costs");
      setItems(data.items || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not load repair costs");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  async function remove(r) {
    if (!confirm(`Delete "${r.title}"?`)) return;
    try {
      await adminApi.delete(`/repair-costs/${r.id}`);
      toast.success("Repair cost deleted");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Delete failed");
    }
  }

  return (
    <div className="space-y-6" data-testid="admin-intel-repair-costs-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <p className="eyebrow">Intelligence platform</p>
          <h1 className="font-display text-[32px] lg:text-[40px] leading-[1.05] tracking-tight text-[#071A33] mt-2">Repair Cost Benchmarks</h1>
          <p className="text-sm text-[#475569] mt-1 max-w-2xl">Internal reference of low / typical / high cost bands for UK park-home work — used by reviewers to benchmark contractor quotes.</p>
        </div>
        <div className="flex gap-2">
          <button onClick={load} className="btn-outline h-10 py-0 px-4 text-sm" data-testid="costs-refresh-btn">
            <RefreshCw size={14}/> Refresh
          </button>
          <button onClick={() => setEditing("new")} className="btn-primary h-10 py-0 px-4 text-sm" data-testid="costs-new-btn">
            <Plus size={14}/> New benchmark
          </button>
        </div>
      </div>

      <div className="card-default p-0 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[#F7F4ED] text-left text-[#475569] text-xs uppercase tracking-[0.18em]">
            <tr>
              <th className="px-4 py-3">Title</th>
              <th className="px-4 py-3">Category</th>
              <th className="px-4 py-3 text-right">Low</th>
              <th className="px-4 py-3 text-right">Typical</th>
              <th className="px-4 py-3 text-right">High</th>
              <th className="px-4 py-3">Status</th>
              <th className="px-4 py-3">Updated</th>
              <th className="px-4 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-[#E2DCD0]">
            {loading && (
              <tr><td colSpan={8} className="px-4 py-10 text-center"><Loader2 className="inline animate-spin" size={16}/></td></tr>
            )}
            {!loading && items.length === 0 && (
              <tr><td colSpan={8} className="px-4 py-10 text-center text-[#475569]">No repair cost benchmarks yet.</td></tr>
            )}
            {!loading && items.map((r) => (
              <tr key={r.id} data-testid={`cost-row-${r.slug}`}>
                <td className="px-4 py-3">
                  <div className="font-medium text-[#071A33] flex items-center gap-2"><BadgePoundSterling size={14} className="text-[#B88A2A]"/>{r.title}</div>
                  <div className="text-xs text-[#94A3B8]">/{r.slug}</div>
                </td>
                <td className="px-4 py-3"><span className="bg-[#0F766E]/10 text-[#0F766E] px-2 py-0.5 rounded text-xs">{r.category}</span></td>
                <td className="px-4 py-3 text-right tabular-nums text-[#475569]">{fmtMoney(r.low_gbp)}</td>
                <td className="px-4 py-3 text-right tabular-nums font-medium text-[#071A33]">{fmtMoney(r.typical_gbp)}</td>
                <td className="px-4 py-3 text-right tabular-nums text-[#475569]">{fmtMoney(r.high_gbp)}</td>
                <td className="px-4 py-3">
                  <span className={`px-2 py-0.5 text-[10px] tracking-[0.1em] uppercase ${r.status === "PUBLISHED" ? "bg-[#064E2E]/10 text-[#064E2E]" : "bg-[#475569]/15 text-[#475569]"}`}>{r.status}</span>
                </td>
                <td className="px-4 py-3 text-xs text-[#475569]">{fmtDate(r.updated_at, true)}</td>
                <td className="px-4 py-3">
                  <div className="flex justify-end gap-1.5">
                    <button onClick={() => setEditing(r)} className="p-2 rounded-full hover:bg-[#F7F4ED]" data-testid={`cost-edit-${r.slug}`}><Pencil size={14} className="text-[#0F766E]"/></button>
                    <button onClick={() => remove(r)} className="p-2 rounded-full hover:bg-[#FCA5A5]/20"><Trash2 size={14} className="text-[#DC2626]"/></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editing && (
        <CostDialog
          initial={editing === "new" ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
    </div>
  );
}

function CostDialog({ initial, onClose, onSaved }) {
  const [form, setForm] = useState(toForm(initial));
  const [busy, setBusy] = useState(false);
  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  async function submit(e) {
    e.preventDefault();
    if (!form.title.trim() || !form.category.trim()) {
      toast.error("Title and category are required");
      return;
    }
    setBusy(true);
    try {
      const payload = toPayload(form);
      if (initial) {
        await adminApi.put(`/repair-costs/${initial.id}`, payload);
        toast.success("Benchmark updated");
      } else {
        await adminApi.post("/repair-costs", payload);
        toast.success("Benchmark created");
      }
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/60 p-4 overflow-y-auto" data-testid="cost-dialog">
      <form onSubmit={submit} className="max-w-3xl mx-auto bg-white rounded-2xl shadow-xl my-6">
        <div className="px-6 py-5 border-b border-[#E2DCD0] flex items-center gap-3">
          <BadgePoundSterling size={20} className="text-[#B88A2A]"/>
          <h2 className="font-display text-2xl text-[#071A33]">{initial ? "Edit benchmark" : "New benchmark"}</h2>
        </div>
        <div className="p-6 space-y-4">
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="sm:col-span-2">
              <label className="field-label">Title *</label>
              <input className="field-input" value={form.title} onChange={(e) => update("title", e.target.value)} required data-testid="cost-title"/>
            </div>
            <div>
              <label className="field-label">Slug</label>
              <input className="field-input" value={form.slug} onChange={(e) => update("slug", e.target.value)} placeholder="auto"/>
            </div>
            <div>
              <label className="field-label">Category *</label>
              <select className="field-input" value={form.category} onChange={(e) => update("category", e.target.value)}>
                {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <label className="field-label">Low (£) *</label>
              <input className="field-input text-right" type="number" min="0" step="0.01" value={form.low_gbp} onChange={(e) => update("low_gbp", e.target.value)} required/>
            </div>
            <div>
              <label className="field-label">Typical (£) *</label>
              <input className="field-input text-right" type="number" min="0" step="0.01" value={form.typical_gbp} onChange={(e) => update("typical_gbp", e.target.value)} required/>
            </div>
            <div className="sm:col-span-2">
              <label className="field-label">High (£) *</label>
              <input className="field-input text-right" type="number" min="0" step="0.01" value={form.high_gbp} onChange={(e) => update("high_gbp", e.target.value)} required/>
            </div>
          </div>
          <div>
            <label className="field-label">Description</label>
            <textarea className="field-input min-h-[90px]" value={form.description} onChange={(e) => update("description", e.target.value)}/>
          </div>
          <div>
            <label className="field-label">Factors affecting price <span className="text-[#94A3B8] font-normal">(one per line)</span></label>
            <textarea className="field-input min-h-[80px]" value={form.factors_affecting_price} onChange={(e) => update("factors_affecting_price", e.target.value)}/>
          </div>
          <div>
            <label className="field-label">Red flags <span className="text-[#94A3B8] font-normal">(one per line)</span></label>
            <textarea className="field-input min-h-[80px]" value={form.red_flags} onChange={(e) => update("red_flags", e.target.value)}/>
          </div>
          <div>
            <label className="field-label">Status</label>
            <select className="field-input" value={form.status} onChange={(e) => update("status", e.target.value)}>
              <option value="PUBLISHED">Published</option>
              <option value="DRAFT">Draft</option>
            </select>
          </div>
        </div>
        <div className="px-6 py-4 bg-[#F7F4ED] border-t border-[#E2DCD0] flex justify-end gap-2 rounded-b-2xl">
          <button type="button" onClick={onClose} className="btn-outline" disabled={busy}>Cancel</button>
          <button type="submit" disabled={busy} className="btn-primary" data-testid="cost-save-btn">
            {busy ? <><Loader2 className="animate-spin" size={16}/> Saving…</> : initial ? "Save changes" : "Create benchmark"}
          </button>
        </div>
      </form>
    </div>
  );
}
